import React, { useState } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Paper,
    InputAdornment,
    IconButton,
    Chip,
    styled,
    Divider,
    Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';

// ─── Styled Components ────────────────────────────────────────────────────────
const FormPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 14,
    boxShadow: '0 4px 24px rgba(25, 118, 210, 0.10)',
    border: '1.5px solid #e3eaf4',
    maxWidth: 480,
    width: '100%',
}));

const FieldLabel = styled(Typography)(() => ({
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#555',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
}));

// ─── Email masking helper ─────────────────────────────────────────────────────
const maskEmail = (email) => {
    const [local, domain] = email.split('@');
    if (!domain) return '***';
    const visible = local.slice(0, 2);
    const domainParts = domain.split('.');
    const tld = domainParts.pop();
    return `${visible}***@***.${tld}`;
};

// ─── Component ────────────────────────────────────────────────────────────────
const ManualBatchForm = ({ onSuccess }) => {
    const [email, setEmail] = useState('');
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [productName, setProductName] = useState('');
    const [productSlug, setProductSlug] = useState('');
    const [exhibitorsCount, setExhibitorsCount] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // ── Email confirm / edit ────────────────────────────────────────────────────
    const handleConfirmEmail = () => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Please enter a valid email address');
            return;
        }
        setEmailConfirmed(true);
    };

    const handleEditEmail = () => {
        setEmailConfirmed(false);
    };

    // ── Submit ──────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!emailConfirmed) {
            toast.warning('Please confirm the email first');
            return;
        }
        if (!productName.trim()) {
            toast.error('Product name is required');
            return;
        }
        const count = parseInt(exhibitorsCount, 10);
        if (!count || count <= 0) {
            toast.error('Please enter a valid exhibitors count');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await axios.post(
                '/api/v1/orders/create-manual-batch',
                {
                    email,
                    product_name: productName.trim(),
                    product_slug: productSlug.trim() || undefined,
                    exhibitors_count: count,
                },
                { withCredentials: true }
            );

            setResult(response.data);
            toast.success('Manual batch created successfully! 🎉');
            if (onSuccess) onSuccess(response.data);

            // Reset form
            setEmail('');
            setEmailConfirmed(false);
            setProductName('');
            setProductSlug('');
            setExhibitorsCount('');
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Something went wrong';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormPaper>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={2.5}>
                <PersonAddIcon sx={{ color: '#1976d2', fontSize: 22 }} />
                <Typography variant="h6" fontWeight={700} color="#1976d2" fontSize="1rem">
                    Create Manual Batch
                </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            {/* ── Email Field ── */}
            <Box mb={2}>
                <FieldLabel>User Email *</FieldLabel>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="user@example.com"
                    value={emailConfirmed ? maskEmail(email) : email}
                    onChange={(e) => !emailConfirmed && setEmail(e.target.value)}
                    disabled={emailConfirmed}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem' },
                        ...(emailConfirmed && {
                            '& .MuiOutlinedInput-root': {
                                background: '#f0f7ff',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                            },
                        }),
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {!emailConfirmed ? (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleConfirmEmail}
                                        disabled={!email}
                                        sx={{
                                            fontSize: '0.7rem',
                                            py: 0.3,
                                            px: 1.2,
                                            borderRadius: '6px',
                                            minWidth: 'auto',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Confirm ✓
                                    </Button>
                                ) : (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                        <IconButton size="small" onClick={handleEditEmail} title="Edit email">
                                            <EditIcon sx={{ fontSize: 16, color: '#888' }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
                {emailConfirmed && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                        ✓ Email confirmed
                    </Typography>
                )}
            </Box>

            {/* ── Product Name ── */}
            <Box mb={2}>
                <FieldLabel>Product Name *</FieldLabel>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. MWC Barcelona Attendees List"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem' } }}
                />
            </Box>

            {/* ── Product Slug (optional) ── */}
            <Box mb={2}>
                <FieldLabel>
                    Product Slug{' '}
                    <Chip label="Optional Fallback" size="small" sx={{ fontSize: '0.65rem', ml: 0.5, height: 16 }} />
                </FieldLabel>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. mwc-barcelona-attendees-list"
                    value={productSlug}
                    onChange={(e) => setProductSlug(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem' } }}
                    helperText="Used only if product is not found by name"
                    FormHelperTextProps={{ sx: { fontSize: '0.7rem', mt: 0.3 } }}
                />
            </Box>

            {/* ── Exhibitors Count ── */}
            <Box mb={3}>
                <FieldLabel>Exhibitors / Attendees Count *</FieldLabel>
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="e.g. 500"
                    value={exhibitorsCount}
                    onChange={(e) => setExhibitorsCount(e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem' } }}
                />
            </Box>

            {/* ── Submit Button ── */}
            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !emailConfirmed}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                sx={{
                    borderRadius: '8px',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    py: 1.2,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    boxShadow: '0 3px 12px rgba(25,118,210,0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                    },
                }}
            >
                {loading ? 'Creating Batch...' : 'Create Manual Batch'}
            </Button>

            {/* ── Error ── */}
            {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '8px', fontSize: '0.8rem' }}>
                    {error}
                </Alert>
            )}

            {/* ── Success Result ── */}
            {result && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: '8px', fontSize: '0.8rem' }}>
                    <Typography variant="body2" fontWeight={600}>Batch Created!</Typography>
                    <Typography variant="caption" display="block">Order ID: #{result.data?.order_id}</Typography>
                    <Typography variant="caption" display="block">Batch ID: #{result.data?.batch?.id}</Typography>
                    <Typography variant="caption" display="block">Product: {result.data?.product?.name}</Typography>
                    <Typography variant="caption" display="block">User: {result.data?.user?.email}</Typography>
                </Alert>
            )}
        </FormPaper>
    );
};

export default ManualBatchForm;
